import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Users, CheckCircle2, XCircle, Clock, ShieldCheck, LogIn } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function StatusBadge({ m }) {
  if (m.subscription_active) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-success/10 text-success" data-testid={`status-${m.id}`}>
        <CheckCircle2 className="w-3 h-3" /> Active · till {fmtDate(m.access_expires_at)}
      </span>
    );
  }
  if (m.access_expires_at) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-destructive/10 text-destructive" data-testid={`status-${m.id}`}>
        <Clock className="w-3 h-3" /> Expired {fmtDate(m.access_expires_at)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-secondary text-muted-foreground" data-testid={`status-${m.id}`}>
      <XCircle className="w-3 h-3" /> No access
    </span>
  );
}

export default function Members() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-members"],
    queryFn: async () => (await api.get("/admin/members")).data,
  });
  const { data: loginData } = useQuery({
    queryKey: ["admin-logins"],
    queryFn: async () => (await api.get("/admin/logins")).data,
  });

  const grantMut = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/members/${id}/grant`, {})).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-members"] }); toast.success("Access granted / renewed"); },
    onError: () => toast.error("Could not grant access"),
  });
  const revokeMut = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/members/${id}/revoke`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-members"] }); toast.success("Access revoked"); },
    onError: () => toast.error("Could not revoke access"),
  });

  const members = data?.members || [];
  const plan = data?.plan;
  const logins = loginData?.logins || [];
  const activeCount = members.filter((m) => m.subscription_active).length;

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="members-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <Users className="w-8 h-8 text-primary" /> Members
        </h1>
        <p className="text-muted-foreground mt-1">
          Everyone who signed up. Grant {plan ? `₹${plan.price_inr.toLocaleString("en-IN")} / ${plan.duration_label}` : ""} access after they pay you directly.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="p-5 rounded-md border border-border bg-card" data-testid="stat-total-members">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Signups</div>
          <div className="font-mono text-3xl font-bold">{members.length}</div>
        </div>
        <div className="p-5 rounded-md border border-border bg-card" data-testid="stat-active-members">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Active</div>
          <div className="font-mono text-3xl font-bold text-success">{activeCount}</div>
        </div>
        <div className="p-5 rounded-md border border-border bg-card" data-testid="stat-inactive-members">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Inactive</div>
          <div className="font-mono text-3xl font-bold text-muted-foreground">{members.length - activeCount}</div>
        </div>
      </div>

      <div className="p-6 rounded-md border border-border bg-card">
        <h3 className="font-heading font-bold text-lg mb-4">Signups & Access</h3>
        {members.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="members-table">
              <thead>
                <tr className="text-left text-muted-foreground font-mono text-xs uppercase tracking-wider border-b border-border">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Joined</th>
                  <th className="py-2 pr-4">Last login</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0" data-testid={`member-row-${m.id}`}>
                    <td className="py-2.5 pr-4 font-medium">{m.name}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{m.email}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground font-mono text-xs">{fmtDate(m.created_at)}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground font-mono text-xs">{fmtDateTime(m.last_login)}</td>
                    <td className="py-2.5 pr-4"><StatusBadge m={m} /></td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <Button
                        size="sm" className="rounded-md gap-1 mr-2"
                        onClick={() => grantMut.mutate(m.id)}
                        disabled={grantMut.isPending}
                        data-testid={`grant-${m.id}`}
                      >
                        <ShieldCheck className="w-3.5 h-3.5" /> {m.subscription_active ? "Renew" : "Grant"}
                      </Button>
                      {(m.subscription_active || m.access_expires_at) && (
                        <Button
                          size="sm" variant="outline" className="rounded-md"
                          onClick={() => revokeMut.mutate(m.id)}
                          disabled={revokeMut.isPending}
                          data-testid={`revoke-${m.id}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No students have signed up yet.</p>
        )}
      </div>

      <div className="p-6 rounded-md border border-border bg-card">
        <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><LogIn className="w-5 h-5 text-primary" /> Recent Logins</h3>
        {logins.length ? (
          <div className="space-y-1.5" data-testid="logins-list">
            {logins.slice(0, 50).map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{l.name || "—"}</span>
                  <span className="text-muted-foreground ml-2">{l.email}</span>
                  {l.role === "admin" && <span className="ml-2 text-xs font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary">admin</span>}
                </div>
                <span className="font-mono text-xs text-muted-foreground shrink-0">{fmtDateTime(l.at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No logins recorded yet.</p>
        )}
      </div>
    </div>
  );
}
